import {
  CustomerServiceOutlined,
  InfoCircleOutlined,
  LogoutOutlined,
  QuestionOutlined,
  SettingFilled,
  SettingOutlined,
} from '@ant-design/icons';
import { Button, Col, Dropdown, Row, Typography } from 'antd';
import BackButton from 'components/buttons/BackButton';
import useIsSmallScreen from 'hooks/useIsSmallScreen';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import type { MenuProps } from 'antd';
import { useAuth } from 'auth/AuthContext';
import { colors } from 'design';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../services/RouteService';

const { Title } = Typography;

export type ChatsHeaderProps = {
  title: React.ReactNode;
  backButton?: boolean;
  onBackButtonClicked?: () => void;
  style?: React.CSSProperties;
};

const useStyles = createUseStyles({
  container: {
    paddingLeft: 13,
    paddingRight: 13,
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    backgroundColor: colors.surface.page,
  },
  // With a back button the title sits right next to it, not at the far end
  containerWithBack: {
    composes: '$container',
    justifyContent: 'flex-start',
    flexWrap: 'nowrap',
  },
  columnBackButton: {
    marginRight: 10,
    alignItems: 'center',
    display: 'flex',
  },
  title: {
    marginTop: 15,
    paddingRight: 13,
    color: colors.text.primary,
    minWidth: 0,
  },
  menuDropdownIcon: {
    fontSize: 25,
    marginRight: 10,
    color: colors.text.primary,
  },
  menuDropdown: {
    '& .ant-dropdown-menu-item': {
      padding: '12px 22px',
    },
  },
  logoutMenuItem: {
    width: '100%',
    color: colors.text.primary,
    padding: 0,
    paddingRight: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 16,
    height: 30,
    '& .anticon': {
      color: colors.text.tertiary,
    },
    '& .danger-icon': {
      color: colors.feedback.error.solid,
    },
  },
});

const PageTopBar: React.FC<ChatsHeaderProps> = ({
  title,
  backButton = false,
  onBackButtonClicked = undefined,
  style,
}) => {
  // Hooks
  const styles = useStyles();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { isSmallScreen } = useIsSmallScreen();
  const { t } = useTranslation();

  const openRoute = (route: string) => {
    navigate(route);
  };

  const settingsMenuItems: MenuProps['items'] = [
    {
      key: '0',
      label: (
        <Button type="link" className={styles.logoutMenuItem} onClick={() => openRoute(routes.settings.route)}>
          <SettingOutlined /> {t('menu.settings')}
        </Button>
      ),
    },
    {
      key: '1',
      label: (
        <Button type="link" className={styles.logoutMenuItem} onClick={() => openRoute(routes.onboarding.route)}>
          <InfoCircleOutlined /> {t('menu.onboarding')}
        </Button>
      ),
    },
    {
      key: '2',
      label: (
        <Button type="link" className={styles.logoutMenuItem} onClick={() => openRoute(routes.faq.route)}>
          <QuestionOutlined /> {t('menu.moreInfo')}
        </Button>
      ),
    },
    {
      key: '3',
      label: (
        <a className={styles.logoutMenuItem} href={`mailto:${t('global.support.supportEmailWithSubject')}`}>
          <CustomerServiceOutlined /> {t('menu.support')}
        </a>
      ),
    },
    {
      key: '4',
      label: (
        <a
          className={styles.logoutMenuItem}
          onClick={() => {
            logout();
          }}
        >
          <LogoutOutlined className="danger-icon" /> {t('menu.logout')}
        </a>
      ),
    },
  ];

  return (
    <Row className={backButton ? styles.containerWithBack : styles.container} style={style}>
      {backButton && (
        <Col className={styles.columnBackButton}>
          <BackButton
            onClick={() => {
              if (onBackButtonClicked) {
                onBackButtonClicked();
              }
            }}
          />
        </Col>
      )}
      <Title level={2} className={styles.title}>
        {title}
      </Title>
      {!backButton && isSmallScreen && (
        <Dropdown classNames={{ root: styles.menuDropdown }} menu={{ items: settingsMenuItems }}>
          <SettingFilled className={styles.menuDropdownIcon} onClick={e => e.preventDefault()} />
        </Dropdown>
      )}
    </Row>
  );
};
export default PageTopBar;
